/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isFloat64Array from '@stdlib/assert-is-float64array';
import hasSameValues from '@stdlib/array-base-assert-has-same-values';


// MAIN //

/**
* Tests if two arguments are both Float64Arrays and have the same values.
*
* @param {*} v1 - first value
* @param {*} v2 - second value
* @returns {boolean} boolean result
*
* @example
* import Float64Array from '@stdlib/array-float64';
*
* var x = new Float64Array( [ 1.0, 2.0, 3.0 ] );
* var y = new Float64Array( [ 1.0, 2.0, 3.0 ] );
*
* var out = isSameFloat64Array( x, y );
* // returns true
*
* @example
* import Float64Array from '@stdlib/array-float64';
*
* var x = new Float64Array( [ 1.0, 2.0, 3.0 ] );
* var y = new Float64Array( [ 1.0, 2.0, 4.0 ] );
*
* var out = isSameFloat64Array( x, y );
* // returns false
*/
function isSameFloat64Array( v1, v2 ) {
	if ( isFloat64Array( v1 ) && isFloat64Array( v2 ) ) {
		return hasSameValues( v1, v2 );
	}
	return false;
}


// EXPORTS //

export default isSameFloat64Array;
